import { Link, useParams } from "react-router-dom";
import Seo, { breadcrumbSchema } from "../components/Seo";
import { Reveal, FrameReveal } from "../components/Reveal";
import CtaBand from "../components/CtaBand";
import { useArticle } from "../lib/hooks";
import { formatArticleDate } from "../lib/site";

// Renders [link text](/path) markdown-style links inside article paragraphs
function renderRichText(text) {
  const parts = String(text).split(/(\[[^\]]+\]\([^)]+\))/g);
  return parts.map((part, i) => {
    const m = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (!m) return part;
    return (
      <Link key={i} to={m[2]} className="text-ember underline underline-offset-4 decoration-ember/40 hover:decoration-ember transition-colors">
        {m[1]}
      </Link>
    );
  });
}


export default function ArticleDetail() {
  const { slug } = useParams();
  const { data: a, isLoading, isError } = useArticle(slug);

  if (isLoading) return <div className="min-h-screen bg-graphite" />;
  if (isError || !a) {
    return (
      <div className="min-h-screen bg-graphite text-white flex items-center justify-center">
        <p data-testid="article-not-found">Article not found. <Link to="/insights" className="text-ember underline">Back to insights</Link>.</p>
      </div>
    );
  }

  const publishedAt = a.published_at || a.updated_at;

  return (
    <div data-testid="article-page">
      <Seo
        title={a.seo_title || `${a.title} | NOOR Insights`}
        description={a.seo_description || a.excerpt}
        path={`/insights/${slug}`}
        image={a.cover}
        imageWidth={1600}
        imageHeight={900}
        noindex={!!a.noindex}
        schema={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Article",
              headline: a.title,
              description: a.seo_description || a.excerpt,
              image: `https://www.noordb.co.uk${a.cover}`,
              datePublished: publishedAt,
              dateModified: a.updated_at || publishedAt,
              author: { "@type": "Organization", name: "NOOR Design & Build Ltd", url: "https://www.noordb.co.uk" },
              publisher: { "@type": "Organization", name: "NOOR Design & Build Ltd", url: "https://www.noordb.co.uk" },
              mainEntityOfPage: `https://www.noordb.co.uk/insights/${slug}`,
            },
            breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Insights", path: "/insights" }, { name: a.title, path: `/insights/${slug}` }]),
          ],
        }}
      />
      <section className="bg-graphite text-white blueprint-grid">
        <div className="max-w-[900px] mx-auto px-5 md:px-10 pt-32 md:pt-44 pb-16 md:pb-20">
          <nav className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/40" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-ember">Home</Link> / <Link to="/insights" className="hover:text-ember">Insights</Link> / <span className="text-ember">Article</span>
          </nav>
          <h1 className="font-display font-light text-3xl sm:text-4xl lg:text-5xl tracking-tighter mt-6 leading-[1.1]">{a.title}</h1>
          {publishedAt && (
            <time data-testid="article-published-date" dateTime={publishedAt} className="block font-mono text-[10px] tracking-[0.22em] uppercase text-white/40 mt-5">
              Published — {formatArticleDate(publishedAt)}
            </time>
          )}
          <p className="text-white/55 text-base md:text-lg leading-relaxed mt-6">{a.excerpt}</p>
        </div>
      </section>
      {a.cover && (
        <div className="max-w-[1100px] mx-auto px-5 md:px-10 -mt-0 bg-cream">
          <div className="border border-black/10 p-2 md:-mt-10 relative bg-cream">
            <FrameReveal src={a.cover} alt={a.title} className="aspect-[16/7]" hoverZoom={false} eager />
          </div>
        </div>
      )}
      <article className="bg-cream">
        <div className="max-w-[760px] mx-auto px-5 md:px-10 py-14 md:py-20">
          {(a.content || []).map((para, i) => (
            <Reveal key={i}>
              {String(para).startsWith("## ") ? (
                <h2 className="font-display font-light text-2xl md:text-3xl tracking-tight text-graphite mt-12 mb-6">
                  {renderRichText(String(para).slice(3))}
                </h2>
              ) : (
                <p className="text-graphite/75 leading-[1.85] text-base md:text-lg mb-8 first:text-xl first:md:text-2xl first:font-display first:font-light first:tracking-tight first:leading-snug">
                  {renderRichText(para)}
                </p>
              )}
            </Reveal>
          ))}
          <Reveal>
            <div className="border border-black/10 bg-stone p-8 mt-12">
              <p className="font-mono text-xs text-ember mb-3">[ Talk to NOOR ]</p>
              <p className="text-graphite/70 text-sm leading-relaxed">
                Planning a project of your own? Request a free initial consultation and we will give you honest,
                informed advice on your home.
              </p>
              <Link to="/request-a-consultation" data-testid="article-consultation-button"
                className="inline-flex items-center gap-2 mt-5 bg-graphite text-white px-6 py-3 text-sm font-semibold hover:bg-ember transition-colors duration-300">
                Request a Consultation
              </Link>
            </div>
          </Reveal>
        </div>
      </article>
      <CtaBand />
    </div>
  );
}
